import querystring from 'query-string'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { axiosFetch, isAxiosCancellation, isAxiosResponseError } from '@halo/_utils/axios'
import { Singleton, Container } from 'typescript-ioc/es5'
import { DomainDto } from '@halo/dtos/domain.dto'
import { simpleNotification } from '@halo/_utils/notifications'
import aqe from '@pushly/aqe'
import { IApiCallOptions, IApiResponse } from '@pushly/aqe/lib/interfaces'
import { Domain } from '@pushly/models/lib/structs/domain'
import { hydrateDomainModel } from '@halo/_utils/models-hydration'

@Singleton
export class DomainService {
    private appState: AppState
    private appService: AppService

    public constructor() {
        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
    }

    public async fetchAll(opts: IApiCallOptions = {}): Promise<IApiResponse<DomainDto[]>> {
        let ok = false
        let domains: DomainDto[] = []
        let meta: any

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const options = querystring.stringify(opts.query || {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/v3/domains?${options}`

        try {
            const res = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                opts.cancellationKey,
            )

            const { data, ...resMeta } = res.data

            ok = true
            domains = data.map(DomainDto.fromApiResponse)
            meta = resMeta
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: domains, meta }
    }

    public async fetchById(domainId: number, opts: IApiCallOptions = {}): Promise<IApiResponse<DomainDto>> {
        let ok = false
        let domain: DomainDto

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const options = querystring.stringify(opts.query || {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/v3/domains/${domainId}?${options}`

        try {
            const req = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                opts.cancellationKey,
            )

            ok = true
            domain = DomainDto.fromApiResponse(req.data.data)
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: domain }
    }

    public async create(createDto: any, opts: IApiCallOptions = {}): Promise<IApiResponse<DomainDto>> {
        let ok = false
        let domain: DomainDto

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        let serviceURL = `${aqe.defaults.publicApiDomain}/domains`
        if (createDto.accountId) {
            serviceURL = `${aqe.defaults.publicApiDomain}/accounts/${createDto.accountId}/domains`
        }

        try {
            const req = await axiosFetch(
                'post',
                {
                    url: serviceURL,
                    data: createDto,
                },
                opts.cancellationKey,
            )

            ok = true
            domain = DomainDto.fromApiResponse(req.data.data)

            simpleNotification('success', `Domain '${domain.name}' has been successfully created.`)
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: domain }
    }

    public async patch(domainId: number, updateDto: any, opts: IApiCallOptions = {}): Promise<IApiResponse<DomainDto>> {
        let ok = false
        let domain: DomainDto

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const serviceURL = `${aqe.defaults.publicApiDomain}/domains/${domainId}`

        try {
            const req = await axiosFetch(
                'patch',
                {
                    url: serviceURL,
                    data: updateDto,
                },
                opts.cancellationKey,
            )

            ok = true
            domain = DomainDto.fromApiResponse(req.data.data)

            simpleNotification('success', `Domain '${domain.name}' has been successfully updated.`)
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: domain }
    }

    /**
     * True Patch using Partial Domain.
     *
     * V1 returns DomainDto as DomainDataForm view uses DTO cased keys
     * @param domainId
     * @param updateDto
     * @param opts
     */
    public async patchByDomainId(
        domainId: number,
        updateDto: Partial<Domain>,
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<DomainDto>> {
        let ok = false
        let domain: Domain
        let legacyDto: DomainDto
        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const serviceURL = `${aqe.defaults.publicApiDomain}/domains/${domainId}`

        try {
            const req = await axiosFetch(
                'patch',
                {
                    url: serviceURL,
                    data: 'serialize' in updateDto ? updateDto.serialize() : updateDto,
                },
                opts.cancellationKey,
            )

            ok = true
            domain = hydrateDomainModel(req.data.data)
            legacyDto = DomainDto.fromApiResponse(req.data.data)

            simpleNotification('success', `Domain '${domain.name}' has been successfully updated.`)
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: legacyDto }
    }

    public async resetDemoDomain(demoId: number | string, opts: IApiCallOptions = {}): Promise<IApiResponse<void>> {
        let ok = false

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const serviceURL = `${aqe.defaults.publicApiDomain}/domains/demoize/${demoId}`

        try {
            const res = await axiosFetch(
                'post',
                {
                    url: serviceURL,
                },
                opts.cancellationKey,
            )

            ok = true

            simpleNotification(
                'success',
                'Your domain is currently refreshing. A domain message will be added shortly once the refresh is completed.',
            )
        } catch (error) {
            if (isAxiosResponseError(error) && !!error?.response?.data?.message) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: undefined }
    }

    public async resetDemoStats(demoId: number | string, opts: IApiCallOptions = {}): Promise<IApiResponse<void>> {
        let ok = false

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const serviceURL = `${aqe.defaults.publicApiDomain}/domains/demoize/stats/${demoId}`

        try {
            const res = await axiosFetch(
                'post',
                {
                    url: serviceURL,
                },
                opts.cancellationKey,
            )

            ok = true

            simpleNotification(
                'success',
                'Your domain is currently refreshing. A domain message will be added shortly once the refresh is completed.',
            )
        } catch (error) {
            if (isAxiosResponseError(error) && !!error?.response?.data?.message) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: undefined }
    }

    public async fetchSegmentFields(domainId: number, opts?: IApiCallOptions) {
        if (opts?.showLoadingScreen) this.appService.setModuleLoading()
        let ok = false
        let fields: any[] = []

        try {
            const req = await axiosFetch(
                'get',
                {
                    url: `${aqe.defaults.publicApiDomain}/v3/domains/${domainId}/segment-fields`,
                },
                opts?.cancellationKey,
            )

            ok = true
            fields = req.data.data
        } catch (error) {
            if (!isAxiosCancellation(error)) {
                simpleNotification('error', error?.response?.data?.message)
            }
        }

        if (opts?.showLoadingScreen) this.appService.unsetModuleLoading()

        return { ok, data: fields }
    }

    public async getCustomMacroFields(domainId: number, opts?: IApiCallOptions) {
        const { ok, data } = await this.fetchSegmentFields(domainId, opts)
        return !ok
            ? []
            : data.map(({ key, name }: { key: string; name: string }) => ({
                  value: key.replace(`.${domainId}_`, '.'),
                  label: name,
              }))
    }
}
